import { addMedia } from "../utils/media-utils";
import { ObjectContentOrigins } from "../object-types";
import configs from "../utils/configs";

AFRAME.registerComponent("external-glb-importer", {
  init() {
    this.hasLoaded = false;

    // Load as soon as the scene is loaded, don't wait for user to enter
    this.el.sceneEl.addEventListener("loaded", () => {
      if (!this.hasLoaded) {
        this.hasLoaded = true;
        // Small delay to ensure everything is initialized
        setTimeout(() => {
          this.loadExternalMedia();
        }, 100);
      }
    });
  },
  async loadExternalMedia() {
    const urlParams = new URLSearchParams(window.location.search);

    // Check for file ID first
    const fileId = urlParams.get("fileId") || urlParams.get("assetId");
    if (fileId) {
      await this.loadFileById(fileId);
      return;
    }

    // Fallback to direct GLB URL
    const glbUrl = urlParams.get("myMediaModel");
    if (glbUrl) {
      // Add hideArtworkLabel flag for direct GLB loads too
      const mediaOptions = { hideArtworkLabel: true };
      const entity = this.spawnMediaInFrontOfPlayerAndReturn(glbUrl, mediaOptions);

      // Automatically enter inspect mode for direct URL loads too
      if (entity) {
        entity.addEventListener(
          "media-loaded",
          () => {
            setTimeout(() => {
              const cameraSystem = this.el.sceneEl.systems["hubs-systems"]?.cameraSystem;
              if (cameraSystem && cameraSystem.inspect) {
                // Lock inspect mode to prevent exiting
                cameraSystem.inspectLocked = true;
                // Mark that this inspect is from external loader
                cameraSystem.inspectFromExternalLoader = true;
                // Use distance modifier of 2.5 to position camera farther away
                cameraSystem.inspect(entity, 2.5, true);

                // If this is an artwork, open the artwork info sidebar
                const hasArtwork = entity.components["artwork"];
                if (hasArtwork) {
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent("open-artwork-info", { detail: entity }));
                  }, 1000);
                }
              }
            }, 500);
          },
          { once: true }
        );

        // Listen for unified media-error and bubble to scene level
        entity.addEventListener(
          "media-error",
          e => {
            console.error("External asset media-error:", e);
            // Bubble error to scene so useRoomLoadingState can catch it
            this.el.sceneEl.emit("media-error", e.detail);
          },
          { once: true }
        );
      }
    }
  },
  async loadFileById(fileId) {
    try {
      // Fetch asset from persistence API
      const assetUrl = `${configs.PERSISTENCE_API_URL}/assets/${fileId}`;
      const response = await fetch(assetUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch asset: ${response.statusText}`);
      }

      const result = await response.json();
      const asset = result.data || result;

      // Determine the URL to use (artwork_model if available, otherwise regular url)
      let urlToLoad = asset.url;
      if (asset?.artwork?.artwork_model) {
        urlToLoad = asset.artwork.artwork_model;
      }

      // Prepare mediaOptions with artwork metadata if available
      const mediaOptions = {};
      if (asset?.artwork || asset?.metadata?.artwork) {
        const artworkData = asset.artwork || asset.metadata.artwork;
        mediaOptions.artworkInfo = JSON.stringify(artworkData);
        mediaOptions.isArtwork = true;
        mediaOptions.hideArtworkLabel = true; // Hide 3D label when loaded from URL
      }

      // Spawn the media with artwork metadata
      const entity = this.spawnMediaInFrontOfPlayerAndReturn(urlToLoad, mediaOptions);

      // Automatically enter inspect mode once the media is loaded
      if (entity) {
        entity.addEventListener(
          "media-loaded",
          () => {
            // Wait a bit for everything to settle, then inspect
            setTimeout(() => {
              const cameraSystem = this.el.sceneEl.systems["hubs-systems"]?.cameraSystem;
              if (cameraSystem && cameraSystem.inspect) {
                // Lock inspect mode to prevent exiting
                cameraSystem.inspectLocked = true;
                // Mark that this inspect is from external loader
                cameraSystem.inspectFromExternalLoader = true;
                // Use distance modifier of 2.5 to position camera farther away
                cameraSystem.inspect(entity, 2.5, true);
              }
            }, 500);
          },
          { once: true }
        );

        // Listen for unified media-error and bubble to scene level
        entity.addEventListener(
          "media-error",
          e => {
            console.error("External asset media-error:", e);
            // Bubble error to scene so useRoomLoadingState can catch it
            this.el.sceneEl.emit("media-error", e.detail);
          },
          { once: true }
        );
      }
    } catch (error) {
      console.error("Error loading file by ID:", error);
      // Emit unified error to scene level for useRoomLoadingState to catch
      this.el.sceneEl.emit("media-error", { src: fileId, error: error.message });
    }
  },
  spawnMediaInFrontOfPlayerAndReturn(src, mediaOptions = {}) {
    const contentOrigin = ObjectContentOrigins.URL;

    const offset = { x: 0, y: 0, z: -1.5 };
    const { entity, orientation } = addMedia(
      src,
      "#static-media",
      contentOrigin,
      typeof src === "object" && src !== null && "name" in src && src.name === "message.png" ? "message" : null,
      !(src instanceof MediaStream),
      true,
      true,
      mediaOptions,
      false // networked = false
    );
    orientation.then(or => {
      entity.setAttribute("offset-relative-to", {
        target: "#avatar-pov-node",
        offset,
        orientation: or
      });
    });

    return entity;
  }
});
